import { defineStore } from 'pinia'
import { getAgentActivities } from '@/common/services/AgentActivityService'
import { getCurrentAgentActivity } from '@/common/services/AgentActivityStatusService'
import { type Activity } from '@/common/types/activity'
import { ref, computed } from 'vue'

export const useActivitiesStore = defineStore('activities', () => {
  const activitiesList = ref<Activity[]>([])

  const noActivityDefault = {
    name: 'assist.activities.no-activity',
    description: 'assist.activities.no-activity.description',
    blockAutomaticPush: false,
    active: true,
    order: 0,
  }

  const userCurrentActivity = ref<Activity>(noActivityDefault)
  const isCurrentActivityReady = ref(false)

  async function fetchAgentActivities() {
    activitiesList.value = await getAgentActivities()
  }

  async function ensureActivitiesReady() {
    if (!activitiesList.value.length) {
      await fetchAgentActivities()
    }
  }

  async function resolveCurrentActivity() {
    if (isCurrentActivityReady.value) {
      return userCurrentActivity.value
    }

    try {
      await ensureActivitiesReady()
      const currentActivity = await getCurrentAgentActivity()
      if (currentActivity.id) {
        userCurrentActivity.value = getAgentActivityById.value(currentActivity.id) ?? noActivityDefault
      }
    } catch (error) {
      console.error(error)
    } finally {
      isCurrentActivityReady.value = true
    }

    return userCurrentActivity.value
  }

  const getAgentAllowingPushActivitiesByAlphabeticalOrder = computed(() => {
    const allowingPushActivities = activitiesList.value?.filter((activity) => !activity.blockAutomaticPush)

    return sortByAlphabeticalOrder(allowingPushActivities)
  })

  const getAgentBlockingPushActivitiesByAlphabeticalOrder = computed(() => {
    const blockingActivities = activitiesList.value?.filter((activity) => activity.blockAutomaticPush)

    return sortByAlphabeticalOrder(blockingActivities)
  })

  const getAgentActivityById = computed(() => {
    return (id: UUID) => activitiesList.value.find((activity) => activity.id === id)
  })
  const sortByAlphabeticalOrder = (activities: Activity[]) => {
    return activities.sort((a, b) => a.name.localeCompare(b.name))
  }

  return {
    userCurrentActivity,
    isCurrentActivityReady,
    activitiesList,
    fetchAgentActivities,
    ensureActivitiesReady,
    resolveCurrentActivity,
    getAgentActivityById,
    noActivityDefault,
    getAgentBlockingPushActivitiesByAlphabeticalOrder,
    getAgentAllowingPushActivitiesByAlphabeticalOrder,
  }
})
